<template>
  <div :style="{ width: $vuetify.breakpoint.smAndDown ? '95%' : '500px', 'margin': '16px auto' }">
    <v-card :style="{ 'width': $vuetify.breakpoint.smAndDown ? 'auto' : '500px'}">
      <div v-if="showNotificationsIcon" style="position: absolute;right: 0;">
        <v-tooltip right>
          <v-btn
            slot="activator"
            :loading="notifyingUsers"
            icon
            ripple
            @click.stop="notify()"
          >
            <v-icon color="secondary">notifications</v-icon>
          </v-btn>
          <span v-t="'editingContainer.notifyUsers'" />
        </v-tooltip>
      </div>

      <v-list subheader>
        <v-subheader inset><span v-t="'editingContainer.date'" /></v-subheader>
        <v-list-tile avatar>
          <v-list-tile-avatar>
            <v-icon large>event</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <date-picker :selected-date="selectedDate" @onDateUpdate="d => selectedDate = d" />
          </v-list-tile-content>
        </v-list-tile>
        <v-subheader inset>
          <span v-t="'editingContainer.providers'" />
          <v-tooltip v-if="providers.length > 0" right>
            <v-icon slot="activator" class="pl-2" color="info" style="font-size: 1.5em;">info</v-icon>
            <span>{{ isTouchDevice ? $t('editingContainer.touchDeviceTooltip') : $t('editingContainer.desktopTooltip') }}</span>
          </v-tooltip>
        </v-subheader>
        <template v-if="providers.length > 0">
          <v-list-tile
            v-for="(provider, index) in providers"
            :key="index"
            :class="showContextMenu && contextMenuProviderId === provider.id ? 'hover': ''"
            avatar
            @click="editProvider(provider.id)"
            @contextmenu="showProviderMenu($event, provider)"
          >
            <v-list-tile-avatar v-if="loadedProviderId === provider.id">
              <v-progress-circular indeterminate color="primary" :width="3" />
            </v-list-tile-avatar>
            <v-list-tile-avatar v-else-if="!provider.approved">
              <v-icon large>pageview</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-avatar v-else>
              <v-icon color="primary" large>check</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title :class="`${provider.color}--text`">{{ provider.name }}</v-list-tile-title>
              <v-list-tile-sub-title>
                {{ provider.approved ? $t('editingContainer.ordersCount', { count: provider.requestsCount }) : $t('editingContainer.notApproved') }}
              </v-list-tile-sub-title>
            </v-list-tile-content>
            <v-list-tile-action v-if="!provider.approved">
              <v-tooltip left>
                <v-btn
                  slot="activator"
                  :loading="approvedProviderId === provider.id"
                  icon
                  ripple
                  @click.stop="approve(provider.id)"
                >
                  <v-icon color="green">check</v-icon>
                </v-btn>
                <span v-t="'editingContainer.approve'" />
              </v-tooltip>
            </v-list-tile-action>
          </v-list-tile>
        </template>
        <template v-else>
          <v-list-tile avatar>
            <v-list-tile-avatar>
              <v-icon large>lightbulb_outline</v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title> <span v-t="'editingContainer.noProviders'" /> </v-list-tile-title>
              <v-list-tile-sub-title v-if="!isFromLastWeek">
                <span v-t="'editingContainer.startBy'" />
                <a @click="addProvider"> <span v-t="'editingContainer.addingOne'" /> </a>
              </v-list-tile-sub-title>
              <v-list-tile-sub-title v-else>
                <span v-t="'editingContainer.cannotAddAnyMore'" />
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
        </template>
      </v-list>
      <v-progress-linear v-if="loadingProviders > 0" height="2" indeterminate class="my-0" />
    </v-card>
    <v-card class="mt-3">
      <v-list>
        <v-subheader inset> <span v-t="'editingContainer.settings'" /> </v-subheader>
        <v-list-tile>
          <v-list-tile-action>
            <v-switch v-model="daySettings.isFreeOfCharge" color="primary" @change="freeLunchSwitch(); saveDaySettings(500)" />
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title><span v-t="'editingContainer.freeOfCharge'" /></v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-list-tile>
          <v-list-tile-action>
            <v-switch v-model="daySettings.rateDishReminder" color="primary" @change="saveDaySettings(500)" />
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title><span v-t="'editingContainer.promptDishRatings'" /></v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-list-tile avatar>
          <v-list-tile-avatar>
            <v-icon large>access_time</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <time-picker
              :order-until-day="orderUntilDay"
              :order-until-hour="orderUntilHour"
              @onTimeChange="onTimeChange"
              @onDayChange="onDayChange"
            />
          </v-list-tile-content>
        </v-list-tile>
        <v-list-tile avatar>
          <v-list-tile-avatar>
            <v-icon large>comment</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-text-field
              v-model="daySettings.comment"
              name="input-7-2"
              :label="$t('editingContainer.comment')"
              @input="saveDaySettings(1000)"
            />
          </v-list-tile-content>
        </v-list-tile>
      </v-list>
      <v-progress-linear v-if="loadingDay > 0 || loadingProviders > 0" height="2" indeterminate class="my-0" />
    </v-card>
    <editing-button
      v-if="!isFromLastWeek"
      @addProvider="addProvider"
      @showLink="showLink"
      @copyProvider="copyProvider"
      @manageProviders="manageProviders"
    />
    <provider-edit-modal
      v-if="editedProviderId !== null"
      :is-free-of-charge="day.isFreeOfCharge"
      :provider-id="editedProviderId"
      :copying-provider="copyingProvider"
      :disable-save="disableProviderSave"
      :selected-language="userData.languageId"
      @close="closeModal"
      @save="saveProvider"
    />
    <copy-provider-modal ref="copyingSelectionModal" @providerSelected="onCopyProviderSelected" />
    <create-link-modal ref="createLinkModal" />
    <manage-providers-modal ref="manageProvidersModal" />
    <v-menu
      v-model="showContextMenu"
      offset-y
      absolute
      :position-x="contextMenuX"
      :position-y="contextMenuY"
    >
      <v-list v-if="!loadingProviders">
        <v-list-tile @click="deleteProvider">
          <v-list-tile-avatar>
            <v-icon>delete</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-title> <span v-t="'common.delete'" /> </v-list-tile-title>
        </v-list-tile>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import gql from 'graphql-tag';
import moment from 'moment';
import some from 'lodash/some';
import NotificationService from '@/services/NotificationService';
import ErrorHandler from '@/services/ErrorHandler';
import PushNotificationsService from '@/services/PushNotificationsService';
import ProviderEditModal from './edit-modal/ProviderEditModal.vue';
import CreateLinkModal from './create-link-modal/CreateLinkModal.vue';
import DatePicker from '../common/date-picker/DatePicker.vue';
import EditingButton from './editing-button/EditingButton.vue';
import CopyProviderModal from './copy-provider-modal/CopyProviderModal.vue';
import ManageProvidersModal from './manage-providers-modal/ManageProvidersModal.vue';
import TimePicker from './time-picker/TimePicker.vue';
import { apolloClient } from '@/apollo';

export default {
  name: 'EditingContainer',
  components: {
    'provider-edit-modal': ProviderEditModal,
    'date-picker': DatePicker,
    'editing-button': EditingButton,
    'time-picker': TimePicker,
    'copy-provider-modal': CopyProviderModal,
    'create-link-modal': CreateLinkModal,
    'manage-providers-modal': ManageProvidersModal,
  },
  data() {
    return {
      selectedDate: this.$route.params.date,
      editedProviderId: null,
      loadedProviderId: 0,
      loadingProviders: 0,
      loadingDay: 0,
      approvedProviderId: 0,
      firstLoad: true,
      day: null,
      daySettings: {},
      showContextMenu: false,
      contextMenuX: 0,
      contextMenuY: 0,
      contextMenuProviderId: null,
      orderUntilDay: null,
      orderUntilHour: null,
      notifyingUsers: false,
      copyingProvider: false,
      saveDaySettingsTimer: null,
      disableProviderSave: false,
    };
  },
  computed: {
    ...mapGetters('userInfoStore', ['userData']),
    ...mapGetters('drawerStore', ['isDrawerOpen']),
    isFromLastWeek() {
      const monday = moment().startOf('isoWeek');
      const selectedDate = moment(this.selectedDate);

      return monday > selectedDate;
    },
    isTouchDevice() {
      if ('ontouchstart' in document.documentElement) {
        return true;
      }

      return false;
    },
    providers() {
      return this.day ? this.day.providers : [];
    },
    showNotificationsIcon() {
      const result = !this.disableEditing() && this.providers.length > 0 && some(this.providers, { approved: true });

      return result;
    },
  },
  watch: {
    selectedDate(newValue) {
      this.$router.push({ params: { date: newValue } });
    },
  },
  methods: {
    closeModal() {
      this.loadedProviderId = 0;
      setTimeout(() => { this.editedProviderId = null; }, 300);
    },
    async saveProvider(provider) {
      this.disableProviderSave = true;
      this.loadedProviderId = 0;

      setTimeout(async () => {
        try {
          this.loadingProviders++;
          // SetTimeout required to proccess saving without bluring some edited field
          await this.$apollo.mutate({
            mutation: gql`mutation SaveProviderQuery($data: ProviderSaveData!) {
                saveProvider(data: $data)
              }`,
            variables: {
              data: this.mapSavedProvider(provider, this.copyingProvider),
            },
          }).then(async () => {
            setTimeout(() => { this.editedProviderId = null; }, 300);
            await this.$apollo.queries.day.refresh();
            await apolloClient.queryManager.refetchQueryByName('DaysQuery');
            NotificationService.showSnackbar({
              message: 'editingContainer.succesfullySaved',
              timeout: 5000,
              position: 'top',
            });
          });
        } catch (error) {
          ErrorHandler.handleApolloError(error);
        } finally {
          this.loadingProviders--;
          this.disableProviderSave = false;
        }
      });
    },
    editProvider(providerId) {
      if (this.disableEditing()) {
        NotificationService.showAlert({
          message: 'editingContainer.unableToEdit',
          level: 'info',
        });
        return;
      }
      this.loadedProviderId = providerId;
      this.editedProviderId = providerId;
      this.copyingProvider = false;
    },
    showLink() {
      this.$refs.createLinkModal.open();
    },
    async copyProvider() {
      this.$refs.copyingSelectionModal.open();
    },
    manageProviders() {
      this.$refs.manageProvidersModal.open();
    },
    addProvider() {
      this.editedProviderId = -1;
    },
    async deleteProvider() {
      if (this.disallowToDeleteProvider(this.providers.find(p => p.id === this.contextMenuProviderId))) {
        NotificationService.showAlert({
          message: 'editingContainer.unableToDeleteProvider',
          level: 'info',
        });
        return;
      }
      this.loadingProviders++;
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation DeleteProviderQuery($providerId: Int!) {
            deleteProvider(providerId: $providerId)
          }`,
          variables: {
            providerId: this.contextMenuProviderId,
          },
        });
        await apolloClient.queryManager.refetchQueryByName('DaysQuery');
        await this.$apollo.queries.day.refresh();
      } catch (error) {
        ErrorHandler.handleApolloError(error);
      }

      this.loadingProviders--;
    },
    onTimeChange(value) {
      if (this.daySettings.orderUntil.format('HH:mm') === value) {
        return;
      }
      const [hours, minutes] = value.split(':');
      this.daySettings.orderUntil.set({ hours, minutes });
      this.orderUntilHour = this.daySettings.orderUntil.format('HH:mm');
      this.saveDaySettings(500);
    },
    onDayChange(value) {
      const [year, month, date] = value.split('-');
      this.daySettings.orderUntil.set({ date, month: month - 1, year });
      this.orderUntilDay = this.daySettings.orderUntil.format('YYYY-MM-DD');
      this.saveDaySettings(500);
    },
    showProviderMenu(e, provider) {
      e.preventDefault();
      this.showContextMenu = false;
      this.contextMenuX = e.clientX;
      this.contextMenuY = e.clientY;
      this.$nextTick(() => {
        this.showContextMenu = true;
        this.contextMenuProviderId = provider.id;
      });
    },
    disallowToDeleteProvider(provider) {
      const result = provider.requestsCount > 0 || this.disableEditing();

      return result;
    },
    disableEditing() {
      const monday = moment().startOf('isoWeek');
      const result = this.day && moment(this.day.date) < monday;

      return result;
    },
    async saveDaySettings(debounce) {
      if (this.disableEditing()) {
        NotificationService.showAlert({
          message: 'editingContainer.unableToEdit',
          level: 'info',
        });
        await this.$apollo.queries.day.refresh();
        return;
      }
      clearTimeout(this.saveDaySettingsTimer);
      this.saveDaySettingsTimer = setTimeout(async () => {
        this.loadingDay = true;
        try {
          await this.$apollo.mutate({
            mutation: gql`mutation SaveDaySettings($data: DaySettings!) {
              updateDaySettings(data: $data)
            }`,
            variables: {
              data: {
                date: this.selectedDate,
                isFreeOfCharge: this.daySettings.isFreeOfCharge,
                orderUntil: this.daySettings.orderUntil.toDate(),
                comment: this.daySettings.comment,
                rateDishReminder: this.daySettings.rateDishReminder,
              },
            },
          });
          await apolloClient.queryManager.refetchQueryByName('DaysQuery');
        } catch (error) {
          ErrorHandler.handleApolloError(error);
        } finally {
          await apolloClient.queryManager.refetchQueryByName('ProvidersOverviewQuery');
        }

        this.loadingDay = false;
      }, debounce);
    },
    async approve(providerId) {
      this.approvedProviderId = providerId;
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ApproveProviderQuery($providerId: Int!) {
            approveProvider(providerId: $providerId)
          }`,
          variables: {
            providerId,
          },
        });
        await apolloClient.queryManager.refetchQueryByName('DaysQuery');
      } catch (error) {
        ErrorHandler.handleApolloError(error);
      }

      this.$apollo.queries.day.refresh();
    },
    async notify() {
      this.notifyingUsers = true;
      await PushNotificationsService.notifyFridayAdded(this.selectedDate);
      this.notifyingUsers = false;
    },
    onCopyProviderSelected(providerId) {
      this.editProvider(providerId);
      this.copyingProvider = true;
    },
    mapSavedProvider(provider, copyingProvider) {
      const result = {
        providerId: copyingProvider ? undefined : provider.id,
        color: provider.color,
        providerDataId: provider.providerDataId,
        dayId: this.day.id,
        categories: provider.categories.map(c => ({
          categoryId: copyingProvider ? undefined : c.id,
          name: c.name,
          translation: c.translation,
          soups: c.soups,
          selections: c.selections.map(s => ({
            selectionId: copyingProvider ? undefined : s.id,
            price: s.price,
            maxCount: s.maxCount || null,
            dishes: s.dishes.map(d => ({
              dishId: copyingProvider ? undefined : d.id,
              name: d.name.trim(),
              translation: !d.translation ? null : d.translation.trim(),
              isSoup: c.soups,
            })),
          })),
        })),
      };
      return result;
    },
    freeLunchSwitch() {
      this.daySettings.rateDishReminder = this.daySettings.isFreeOfCharge;
    },
  },
  apollo: {
    $loadingKey: 'loadingProviders',
    day: {
      fetchPolicy: 'network-only',
      query: gql`query ProvidersOverviewQuery($date: TimelessDate!, $timezoneOffset: Int!) {
        day(date: $date, timezoneOffset: $timezoneOffset, findOrCreate: true) {
          id
          orderUntil
          isFreeOfCharge
          date
          comment
          rateDishReminder
          providers(onlyApproved: false) {
            id
            requestsCount
            color
            name
            approved
          }
        }
      }`,
      variables() {
        return {
          date: this.selectedDate,
          timezoneOffset: moment(this.selectedDate).utcOffset(),
        };
      },
      skip() {
        return !this.selectedDate;
      },
      error(error) {
        this.day = null;
        ErrorHandler.handleApolloError(error);
      },
      result(r) {
        if (!r.loading) {
          this.approvedProviderId = 0;
          this.firstLoad = false;
          NotificationService.clearNetworkNotification();

          if (r.data.day) {
            this.daySettings = {
              isFreeOfCharge: r.data.day.isFreeOfCharge,
              orderUntil: moment(r.data.day.orderUntil),
              comment: r.data.day.comment,
              rateDishReminder: r.data.day.rateDishReminder,
            };
            this.orderUntilHour = this.daySettings.orderUntil.format('HH:mm');
            this.orderUntilDay = this.daySettings.orderUntil.format('YYYY-MM-DD');
          } else {
            this.daySettings = null;
          }
        }
      },
    },
  },
};
</script>
<style scoped>
  .hover {
    background: rgba(245,245,245,255)
  }
</style>
